import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { TSetListAction } from 'types/types';
import {
  DEFAULT_PAGE,
  DEFAULT_PER_PAGE,
  DEFAULT_PER_PAGE_ALL,
  IndexedObject,
  TActionParams,
  TOptionsQuery,
} from 'types/common';
import { setShowToast } from 'store/toast/toastSlice';
import { ETypeToast } from 'app/enum';
import {
  TCalenderFree,
  TEmployeeManagement,
  TImplement,
} from 'types/clientManagement/clientManagement';
import { additionalInterviewApi } from 'api/additionalInterviewApi';
import { t } from 'i18next';
import { translations } from '../../locales/translations';
import { convertValueToNumber, getLabelError } from '../../helpers/funcs';
import { consultantInterviewApi } from '../../api/consultantInterview';
import moment from 'moment';

export type AdditionalInterviewState = {
  loading: boolean;
  listAdditionalInterview: TImplement[];
  listEmployeeProgress: TImplement[];
  additionalInterviewDetail: TImplement;
  additionalInterviewGroupDetail: TImplement[];
  listAdditionalInterviewByEmployee: TImplement[];
  listAdditionalInterviewByConsultant: TImplement[];
  total: number;
  page: number;
  totalPage: number;
  totalEmployeeProgress: number;
  pageEmployeeProgress: number;
  isShowListEvent: boolean;
  facilityNameSelected: string;
  employeeNameSelected: string;
  groupMultiSelected: string[];
  listEmployeeNotJoin: TEmployeeManagement[];
  listCalenderFree: TCalenderFree[];
  listConsultantsFree: any;
  listAllConsultants: any;
  messageErr: string;
  currentLoad: boolean;
  loadingDownload: boolean;
  loadingDownloadCSV: boolean;
};

const initialState: AdditionalInterviewState = {
  loading: false,
  listAdditionalInterview: [],
  additionalInterviewDetail: {},
  additionalInterviewGroupDetail: [],
  listEmployeeProgress: [],
  listAdditionalInterviewByEmployee: [],
  listAdditionalInterviewByConsultant: [],
  total: 0,
  page: 1,
  totalPage: 1,
  totalEmployeeProgress: 0,
  pageEmployeeProgress: 1,
  isShowListEvent: false,
  facilityNameSelected: '',
  employeeNameSelected: '',
  groupMultiSelected: ['All'],
  listEmployeeNotJoin: [],
  listCalenderFree: [],
  listConsultantsFree: [],
  listAllConsultants: [],
  messageErr: '',
  currentLoad: false,
  loadingDownload: false,
  loadingDownloadCSV: false,
};

export const DEFAULT_ORDER_BY_ADDITIONAL_INTERVIEW = 'createdAt';
export const DEFAULT_ORDER_DIRECTION_ADDITIONAL_INTERVIEW = 'desc';
export const getListAdditionalInterview = createAsyncThunk(
  'additionalInterview/getListAdditionalInterview',
  async (params: TOptionsQuery<TImplement>, thunkAPI) => {
    thunkAPI.dispatch(setLoading(true));
    const { loadMore, ...rest } = params;
    const newParams: TOptionsQuery<TImplement> = {
      ...rest,
      orderBy: rest.orderBy ?? DEFAULT_ORDER_BY_ADDITIONAL_INTERVIEW,
      orderDirection: rest.orderDirection ?? DEFAULT_ORDER_DIRECTION_ADDITIONAL_INTERVIEW,
      page: rest.page ?? DEFAULT_PAGE,
      perPage: rest.perPage ?? DEFAULT_PER_PAGE,
    };
    await additionalInterviewApi
      .getListAdditionalInterview(newParams)
      .then((response) => {
        thunkAPI.dispatch(
          setListAdditionalInterview({
            type: loadMore ? 'loadMore' : 'getList',
            additionalInterviewS: response.data.data,
            total: response.data._metadata.pagination.total,
            page: response.data._metadata.pagination.page,
            totalPage: response.data._metadata.pagination.totalPage,
          }),
        );
      })
      .catch((response) => {
        thunkAPI.dispatch(
          setShowToast({
            label: getLabelError(response.response.data.statusCode),
            type: ETypeToast.Error,
          }),
        );
        thunkAPI.dispatch(setLoading(false));
      });

    thunkAPI.dispatch(setLoading(false));
  },
);

export const registerAdditionalInterview = createAsyncThunk(
  'additionalInterview/registerAdditionalInterview',
  async (actionParams: TActionParams<TImplement>, thunkAPI) => {
    thunkAPI.dispatch(setLoading(true));
    await additionalInterviewApi
      .registerAdditionalInterview(actionParams.data)
      .then(() => {
        thunkAPI.dispatch(resetListAdditionalInterview());
        thunkAPI.dispatch(setPage(1));
        thunkAPI.dispatch(setCurrentLoad(true));
        thunkAPI.dispatch(resetErrMsg());
        thunkAPI.dispatch(
          setShowToast({
            label: t(translations.ID_MASTER.REGISTER_SUCCESS),
            type: ETypeToast.Success,
          }),
        );
        actionParams.funct && actionParams.funct();
      })
      .catch((response) => {
        if (response.response.data.statusCode === 111230) {
          thunkAPI.dispatch(setErrMsg(response.response.data));
          thunkAPI.dispatch(setLoading(false));
        }
      });
    thunkAPI.dispatch(setLoading(false));
  },
);

export const updateAdditionalInterview = createAsyncThunk(
  'additionalInterview/updateAdditionalInterview',
  async (params: { id: string; data: TActionParams<TImplement> }, thunkAPI) => {
    thunkAPI.dispatch(setLoading(true));
    await additionalInterviewApi
      .updateAdditionalInterview({ id: params.id, data: params.data.data })
      .then((response: any) => {
        thunkAPI.dispatch(
          setListAdditionalInterview({
            type: 'edit',
            additionalInterview: { ...params.data.data, _id: response.data.data._id },
          }),
        );
        thunkAPI.dispatch(getListAdditionalInterview({}));
        thunkAPI.dispatch(
          setShowToast({
            label: t(translations.ID_MASTER.UPDATE_SUCCESS),
            type: ETypeToast.Success,
          }),
        );
        params.data.funct && params.data.funct();
      })
      .catch((response) => {
        if (response.response.data.statusCode === 111230) {
          thunkAPI.dispatch(setErrMsg(response.response.data));
          thunkAPI.dispatch(setLoading(false));
        }
      });
    thunkAPI.dispatch(setLoading(false));
  },
);

export const deleteAdditionalInterview = createAsyncThunk(
  'additionalInterview/deleteAdditionalInterview',
  async (additionalInterview: TImplement, thunkAPI) => {
    thunkAPI.dispatch(setLoading(true));
    await additionalInterviewApi
      .deleteAdditionalInterview(String(additionalInterview._id))
      .then(() => {
        thunkAPI.dispatch(
          setListAdditionalInterview({ type: 'delete', additionalInterview: additionalInterview }),
        );
        thunkAPI.dispatch(
          setShowToast({
            label: t(translations.ID_MASTER.DELETE_SUCCESS),
            type: ETypeToast.Success,
          }),
        );
      })
      .catch((response) => {
        thunkAPI.dispatch(
          setShowToast({
            label: getLabelError(response.response.data.statusCode),
            type: ETypeToast.Error,
          }),
        );
        thunkAPI.dispatch(setLoading(false));
      });
    thunkAPI.dispatch(setLoading(false));
  },
);

export const getEmployeeProgress = createAsyncThunk(
  'additionalInterview/getEmployeeProgress',
  async (params: TOptionsQuery<TImplement>, thunkAPI) => {
    thunkAPI.dispatch(setLoading(true));
    const { loadMore, ...rest } = params;
    if (rest.facilityId === '') {
      thunkAPI.dispatch(
        setListEmployeeProgress({
          type: 'getList',
          employeeProgressS: [],
          total: 0,
          page: 1,
        }),
      );
    } else {
      const newParams: TOptionsQuery<TImplement> = {
        ...rest,
        orderBy: rest.orderBy ?? DEFAULT_ORDER_BY_ADDITIONAL_INTERVIEW,
        orderDirection: rest.orderDirection ?? DEFAULT_ORDER_DIRECTION_ADDITIONAL_INTERVIEW,
        page: rest.page ?? DEFAULT_PAGE,
        perPage: rest.perPage ?? DEFAULT_PER_PAGE_ALL,
        groupBy: rest.groupBy ?? 'employee',
      };
      await additionalInterviewApi
        .getEmployeeProgress(newParams)
        .then((response) => {
          thunkAPI.dispatch(
            setListEmployeeProgress({
              type: loadMore ? 'loadMore' : 'getList',
              employeeProgressS: response.data.data,
              total: response.data._metadata.pagination.total,
              page: response.data._metadata.pagination.page,
            }),
          );
        })
        .catch((response) => {
          thunkAPI.dispatch(
            setShowToast({
              label: getLabelError(response.response.data.statusCode),
              type: ETypeToast.Error,
            }),
          );
          thunkAPI.dispatch(setLoading(false));
        });
    }

    thunkAPI.dispatch(setLoading(false));
  },
);

export const deleteEmployeeProgress = createAsyncThunk(
  'additionalInterview/deleteEmployeeProgress',
  async (employeeProgress: TImplement, thunkAPI) => {
    thunkAPI.dispatch(setLoading(true));
    await additionalInterviewApi
      .deleteEmployeeProgress(String(employeeProgress?._id), employeeProgress?.eventId ?? '')
      .then(() => {
        thunkAPI.dispatch(
          setListEmployeeProgress({ type: 'delete', employeeProgress: employeeProgress }),
        );
        thunkAPI.dispatch(
          setShowToast({
            label: t(translations.ID_MASTER.DELETE_SUCCESS),
            type: ETypeToast.Success,
          }),
        );
      })
      .catch((response) => {
        thunkAPI.dispatch(
          setShowToast({
            label: getLabelError(response.response.data.statusCode),
            type: ETypeToast.Error,
          }),
        );
        thunkAPI.dispatch(setLoading(false));
      });
    thunkAPI.dispatch(setLoading(false));
  },
);

export const updateEmployeeProgress = createAsyncThunk(
  'additionalInterview/updateEmployeeProgress',
  async (params: { id: string; data: TActionParams<TImplement> }, thunkAPI) => {
    thunkAPI.dispatch(setLoading(true));
    await additionalInterviewApi
      .updateEmployeeProgress({ id: params.id, data: params.data.data })
      .then((response: any) => {
        thunkAPI.dispatch(
          setListEmployeeProgress({
            type: 'edit',
            employeeProgress: { ...params.data.data, _id: response.data.data._id },
          }),
        );
        thunkAPI.dispatch(
          setShowToast({
            label: t(translations.ID_MASTER.UPDATE_SUCCESS),
            type: ETypeToast.Success,
          }),
        );
      })
      .catch((response) => {
        thunkAPI.dispatch(
          setShowToast({
            label: getLabelError(response.response.data.statusCode),
            type: ETypeToast.Error,
          }),
        );
        thunkAPI.dispatch(setLoading(false));
      });
    thunkAPI.dispatch(setLoading(false));
  },
);

export const cancelInterview = createAsyncThunk(
  'additionalInterview/cancelInterview',
  async (params: { id: string; cancelReason: string; isCom: boolean }, thunkAPI) => {
    const { id, cancelReason, isCom } = params;
    thunkAPI.dispatch(setLoading(true));
    await consultantInterviewApi
      .cancelAdditionalInterview(id, { cancelReason, isCom })
      .then(() => {
        thunkAPI.dispatch(
          setShowToast({
            label: t(translations.ID_MASTER.CANCEL_SUCCESS),
            type: ETypeToast.Success,
          }),
        );
      })
      .catch((response) => {
        thunkAPI.dispatch(
          setShowToast({
            label: getLabelError(response.response.data.statusCode),
            type: ETypeToast.Error,
          }),
        );
        thunkAPI.dispatch(setLoading(false));
      });
    thunkAPI.dispatch(setLoading(false));
  },
);

export const getAdditionalInterviewDetail = createAsyncThunk(
  'additionalInterview/getAdditionalInterviewDetail',
  async (id: string) => {
    const response = await additionalInterviewApi.getAdditionalInterviewDetail(id);
    return response.data.data;
  },
);

export const getAdditionalInterviewGroupDetail = createAsyncThunk(
  'additionalInterview/getAdditionalInterviewGroupDetail',
  async (params: any) => {
    const response = await additionalInterviewApi.getAdditionalInterviewGroupDetail(params);
    const data = response.data.data;
    if (params.selected && !params.selected.includes('All')) {
      return data.filter((obj) => params.selected.includes(obj.facilityId));
    }
    return data;
  },
);

export const getListAdditionalInterviewByEmployee = createAsyncThunk(
  'additionalInterview/getListAdditionalInterviewByEmployee',
  async (id: string) => {
    const response = await additionalInterviewApi.getListEventByEmployee(id);
    return response.data.data;
  },
);

export const getListAdditionalInterviewByEmployeeAndConsultant = createAsyncThunk(
  'additionalInterview/getListAdditionalInterviewByEmployeeAndConsultant',
  async (params: any) => {
    const response = await additionalInterviewApi.getListEventByConsultantAndEmployee(
      params.employeeId,
      params.consultantId,
    );
    return response.data.data;
  },
);

export const getListEmployeeNotJoin = createAsyncThunk(
  'additionalInterview/getListEmployeeNotJoin',
  async (id: string) => {
    const response = await additionalInterviewApi.getListEmployeeNotJoin(id);
    return response.data.data.employees;
  },
);

export const addListEmployeeJoinEvent = createAsyncThunk(
  'additionalInterview/addListEmployeeJoinEvent',
  async (data: any[], thunkAPI) => {
    thunkAPI.dispatch(setLoading(true));
    await additionalInterviewApi
      .addListEmployeeJoinEvent(data)
      .then(() => {
        thunkAPI.dispatch(
          getEmployeeProgress({
            eventId: data[0].eventId ?? '',
          }),
        );
        thunkAPI.dispatch(
          setShowToast({
            label: t(translations.ID_MASTER.REGISTER_SUCCESS),
            type: ETypeToast.Success,
          }),
        );
      })
      .catch((response) => {
        thunkAPI.dispatch(
          setShowToast({
            label: getLabelError(response.response.data.statusCode),
            type: ETypeToast.Error,
          }),
        );
        thunkAPI.dispatch(setLoading(false));
      });
    thunkAPI.dispatch(setLoading(false));
  },
);

export const updateConsultant = createAsyncThunk(
  'additionalInterview/updateConsultant',
  async (
    params: { implementId: string; employeeProgressId: string; listConsultantId: Array<string> },
    thunkAPI,
  ) => {
    thunkAPI.dispatch(setLoading(true));
    await additionalInterviewApi
      .updateConsultant({
        id: params.implementId,
        data: { progressId: params.employeeProgressId, consultantId: params.listConsultantId },
      })
      .then(() => {
        thunkAPI.dispatch(
          setShowToast({
            label: t(translations.ID_MASTER.UPDATE_SUCCESS),
            type: ETypeToast.Success,
          }),
        );
      })
      .catch((response) => {
        thunkAPI.dispatch(
          setShowToast({
            label: getLabelError(response.response.data.statusCode),
            type: ETypeToast.Error,
          }),
        );
        thunkAPI.dispatch(setLoading(false));
      });
    thunkAPI.dispatch(setLoading(false));
  },
);

export const getCalendarInterview = createAsyncThunk(
  'additionalInterview/getCalendarInterview',
  async (params: { employeeProgressId: string; eventId: string; isCom: boolean }, thunkAPI) => {
    thunkAPI.dispatch(setLoading(true));
    await additionalInterviewApi
      .getCalendarInterview(params)
      .then((res) => {
        thunkAPI.dispatch(setListCalenderFree(res.data.data));
      })
      .catch((response) => {
        thunkAPI.dispatch(
          setShowToast({
            label: getLabelError(response.response.data.statusCode),
            type: ETypeToast.Error,
          }),
        );
        thunkAPI.dispatch(setLoading(false));
      });
    thunkAPI.dispatch(setLoading(false));
  },
);

export const getSpecificCalendarInterview = createAsyncThunk(
  'additionalInterview/getSpecificCalendarInterview',
  async (
    params: { eventId: string; employeeProgressId: string; consultantId: string; isCom: boolean },
    thunkAPI,
  ) => {
    thunkAPI.dispatch(setLoading(true));
    await additionalInterviewApi
      .getSpecificCalendarInterview(
        params.eventId,
        params.employeeProgressId,
        params.consultantId,
        params.isCom,
      )
      .then((res: any) => {
        thunkAPI.dispatch(setListCalenderFree(res.data.data));
      })
      .catch((response) => {
        thunkAPI.dispatch(
          setShowToast({
            label: getLabelError(response.response.data.statusCode),
            type: ETypeToast.Error,
          }),
        );
        thunkAPI.dispatch(setLoading(false));
      });
    thunkAPI.dispatch(setLoading(false));
  },
);

export const getConsultantInterview = createAsyncThunk(
  'additionalInterview/getConsultantInterview',
  async (params: { employeeProgressId: string; dataBody: any }, thunkAPI) => {
    thunkAPI.dispatch(setLoading(true));
    await additionalInterviewApi
      .getConsultantInterview(params.employeeProgressId, params.dataBody)
      .then((res) => {
        thunkAPI.dispatch(setListConsultantsFree(res.data.data));
      })
      .catch((response) => {
        thunkAPI.dispatch(
          setShowToast({
            label: getLabelError(response.response.data.statusCode),
            type: ETypeToast.Error,
          }),
        );
        thunkAPI.dispatch(setLoading(false));
      });
    thunkAPI.dispatch(setLoading(false));
  },
);

export const getAllConsultantInterview = createAsyncThunk(
  'additionalInterview/getAllConsultantInterview',
  async (params: { employeeProgressId: string; eventId: string }, thunkAPI) => {
    thunkAPI.dispatch(setLoading(true));
    await additionalInterviewApi
      .getAllConsultantInterview(params.employeeProgressId, params.eventId)
      .then((res) => {
        thunkAPI.dispatch(setListAllConsultants(res.data.data));
      })
      .catch((response) => {
        thunkAPI.dispatch(
          setShowToast({
            label: getLabelError(response.response.data.statusCode),
            type: ETypeToast.Error,
          }),
        );
        thunkAPI.dispatch(setLoading(false));
      });
    thunkAPI.dispatch(setLoading(false));
  },
);

export const bookConsultantInterview = createAsyncThunk(
  'additionalInterview/bookConsultantInterview',
  async (
    data: {
      eventId: string;
      consultantId: string;
      employeeId: string;
      startTime: string;
      endTime: string;
      isCom: boolean;
    },
    thunkAPI,
  ) => {
    thunkAPI.dispatch(setLoading(true));
    await additionalInterviewApi
      .bookConsultantInterview(data)
      .then((res) => {
        thunkAPI.dispatch(
          setShowToast({
            label: t(translations.ID_MASTER.BOOK_INTERVIEW_SUCCESS),
            type: ETypeToast.Success,
          }),
        );
        thunkAPI.dispatch(setLoading(false));
      })
      .catch((response) => {
        thunkAPI.dispatch(
          setShowToast({
            label: getLabelError(response.response.data.statusCode),
            type: ETypeToast.Error,
          }),
        );
        thunkAPI.dispatch(setLoading(false));
      });
    thunkAPI.dispatch(setLoading(false));
  },
);

export const downloadFileFeedback = createAsyncThunk(
  'additionalInterview/downloadFileFeedback',
  async (params: any, thunkAPI) => {
    try {
      thunkAPI.dispatch(setLoadingDownload(true));
      await additionalInterviewApi.downloadFileFeedback(params.eventId).then((response) => {
        const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = `${params.eventName}_${params.facilityName}_${
          params.clientName
        }_${moment().format('YYYYMMDD')}.zip`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(downloadUrl);
      });
      thunkAPI.dispatch(setLoadingDownload(false));
    } catch (e) {
      thunkAPI.dispatch(setLoadingDownload(false));
    }
  },
);

export const resetInterview = createAsyncThunk(
  'additionalInterview/resetMeeting',
  async (params: { eventId: string; employeeId: string; employeeProgressId: string }, thunkAPI) => {
    await additionalInterviewApi
      .resetMeeting(params.eventId, params.employeeId, params.employeeProgressId)
      .then((response) => {
        thunkAPI.dispatch(
          getEmployeeProgress({
            eventId: params.eventId,
          }),
        );
      });
    thunkAPI.dispatch(
      setShowToast({
        label: t(translations.ID_MASTER.RESET_MEETING_SUCCESS),
        type: ETypeToast.Success,
      }),
    );
    thunkAPI.dispatch(setLoading(false));
  },
);

export const downloadFileCSV = createAsyncThunk(
  'additionalInterview/downloadFileCSV',
  async (params: any, thunkAPI) => {
    try {
      thunkAPI.dispatch(setLoadingDownloadCSV(true));
      await additionalInterviewApi.downloadFileCsv(params.eventId).then((response) => {
        const downloadUrl = URL.createObjectURL(response.data);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = `${params.eventName}_${params.facilityName}_${
          params.clientName
        }_${moment().format('YYYYMMDD')}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(downloadUrl);
      });
      thunkAPI.dispatch(setLoadingDownloadCSV(false));
    } catch (e) {
      thunkAPI.dispatch(setLoadingDownloadCSV(false));
    }
  },
);

export const downloadFileAllFeedback = createAsyncThunk(
  'additionalInterview/downloadFileAllFeedback',
  async (params: TOptionsQuery<TImplement>, thunkAPI) => {
    const { loadMore, clientName, ...rest } = params;
    const newParams: TOptionsQuery<TImplement> = {
      ...rest,
      orderBy: rest.orderBy ?? DEFAULT_ORDER_BY_ADDITIONAL_INTERVIEW,
      orderDirection: rest.orderDirection ?? DEFAULT_ORDER_DIRECTION_ADDITIONAL_INTERVIEW,
      page: rest.page ?? DEFAULT_PAGE,
      perPage: rest.perPage ?? DEFAULT_PER_PAGE,
    };

    const ranger =
      newParams.ranger?.length === 0
        ? ''
        : '_' + newParams.ranger?.map((date) => date.replace(/\//g, '')).join('_');

    try {
      thunkAPI.dispatch(setLoading(true));
      await additionalInterviewApi.downloadFileAllFeedback(newParams).then((response) => {
        const downloadUrl = URL.createObjectURL(response.data);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = `${clientName}_Feedback${ranger}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(downloadUrl);
      });
      thunkAPI.dispatch(setLoading(false));
    } catch (e) {
      thunkAPI.dispatch(setLoading(false));
    }
  },
);

const slice = createSlice({
  name: 'additionalInterview',
  initialState,
  reducers: {
    setListAdditionalInterview: (
      state,
      action: PayloadAction<{
        type: TSetListAction;
        additionalInterview?: TImplement;
        additionalInterviewS?: TImplement[];
        page?: number;
        total?: number;
        totalPage?: number;
      }>,
    ) => {
      const { type, additionalInterview, additionalInterviewS, total, page, totalPage } =
        action.payload;
      if (additionalInterview) {
        switch (type) {
          case 'create':
            state.listAdditionalInterview = [
              { ...additionalInterview, isActive: true },
              ...state.listAdditionalInterview,
            ];
            break;
          case 'edit':
            state.listAdditionalInterview = state.listAdditionalInterview.map((m: TImplement) =>
              m._id === additionalInterview._id ? { ...additionalInterview, isActive: true } : m,
            );
            break;
          case 'delete':
            state.listAdditionalInterview = state.listAdditionalInterview.filter(
              (m: TImplement) => m._id !== additionalInterview._id,
            );
            state.total -= 1;
            break;
          case 'stop':
            state.listAdditionalInterview = state.listAdditionalInterview.map((m: TImplement) =>
              m._id === additionalInterview._id
                ? { ...additionalInterview, isActive: !additionalInterview.isActive }
                : m,
            );
        }
      }
      if (additionalInterviewS) {
        switch (type) {
          case 'getList':
            state.listAdditionalInterview = additionalInterviewS;
            break;
          case 'loadMore':
            state.listAdditionalInterview = [
              ...state.listAdditionalInterview,
              ...additionalInterviewS,
            ];
            break;
        }

        state.page = page ?? DEFAULT_PAGE;
        state.total = convertValueToNumber(total);
        state.totalPage = convertValueToNumber(totalPage);
      }
    },
    setListEmployeeProgress: (
      state,
      action: PayloadAction<{
        type: TSetListAction;
        employeeProgress?: TImplement;
        employeeProgressS?: TImplement[];
        page?: number;
        total?: number;
      }>,
    ) => {
      const { type, employeeProgress, employeeProgressS, total, page } = action.payload;
      if (employeeProgress) {
        switch (type) {
          case 'edit':
            state.listEmployeeProgress = state.listEmployeeProgress.map((m: TImplement) =>
              m._id === employeeProgress._id ? { ...employeeProgress, isActive: true } : m,
            );
            break;
          case 'delete':
            state.listEmployeeProgress = state.listEmployeeProgress.filter(
              (m: TImplement) => m._id !== employeeProgress._id,
            );
            break;
        }
      }
      if (employeeProgressS) {
        switch (type) {
          case 'getList':
            state.listEmployeeProgress = employeeProgressS;
            break;
          case 'loadMore':
            state.listEmployeeProgress = [...state.listEmployeeProgress, ...employeeProgressS];
            break;
        }

        state.pageEmployeeProgress = page ?? DEFAULT_PAGE;
        state.totalEmployeeProgress = total ?? 0;
      }
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setGroupMultiSelected: (state, action: PayloadAction<Array<string>>) => {
      state.groupMultiSelected = action.payload;
    },
    setIsShowListEvent: (state, action: PayloadAction<boolean>) => {
      state.isShowListEvent = action.payload;
    },
    setFacilityName: (state, action: PayloadAction<string>) => {
      state.facilityNameSelected = action.payload;
    },
    setEmployeeName: (state, action: PayloadAction<string>) => {
      state.employeeNameSelected = action.payload;
    },
    setListAdditionalInterviewByFacility: (state, action: PayloadAction<Array<TImplement>>) => {
      state.listAdditionalInterview = action.payload;
    },
    setListCalenderFree: (state, action: PayloadAction<Array<TCalenderFree>>) => {
      state.listCalenderFree = action.payload;
    },
    setListConsultantsFree: (state, action: PayloadAction<Array<TCalenderFree>>) => {
      state.listConsultantsFree = action.payload;
    },
    setListAllConsultants: (state, action: PayloadAction<Array<any>>) => {
      state.listAllConsultants = action.payload;
    },

    setErrMsg: (state, action: PayloadAction<IndexedObject>) => {
      const { message, data } = action.payload ?? {};
      if (message && data) {
        state.messageErr = data.employeeNames?.length
          ? message + '<br/>' + data.employeeNames.join('<br/>')
          : message;
      } else {
        state.messageErr = 'この期間中にイベントが既に存在しています。';
      }
    },
    resetErrMsg: (state) => {
      state.messageErr = '';
    },
    resetListAdditionalInterview: (state) => {
      state.listAdditionalInterview = [];
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    setCurrentLoad: (state, action: PayloadAction<boolean>) => {
      state.currentLoad = action.payload;
    },
    setLoadingDownload: (state, action: PayloadAction<boolean>) => {
      state.loadingDownload = action.payload;
    },
    setLoadingDownloadCSV: (state, action: PayloadAction<boolean>) => {
      state.loadingDownloadCSV = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getAdditionalInterviewDetail.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(
      getAdditionalInterviewDetail.fulfilled,
      (state, action: PayloadAction<TImplement>) => {
        state.loading = false;
        state.additionalInterviewDetail = action.payload;
      },
    );

    builder.addCase(getAdditionalInterviewGroupDetail.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(
      getAdditionalInterviewGroupDetail.fulfilled,
      (state, action: PayloadAction<TImplement[]>) => {
        state.loading = false;
        state.additionalInterviewGroupDetail = action.payload;
      },
    );

    builder.addCase(getListAdditionalInterviewByEmployee.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(
      getListAdditionalInterviewByEmployee.fulfilled,
      (state, action: PayloadAction<TImplement[]>) => {
        state.loading = false;
        state.listAdditionalInterviewByEmployee = action.payload;
      },
    );
    builder.addCase(getListAdditionalInterviewByEmployeeAndConsultant.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(
      getListAdditionalInterviewByEmployeeAndConsultant.fulfilled,
      (state, action: PayloadAction<TImplement[]>) => {
        state.loading = false;
        state.listAdditionalInterviewByConsultant = action.payload;
      },
    );
    builder.addCase(getListEmployeeNotJoin.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(
      getListEmployeeNotJoin.fulfilled,
      (state, action: PayloadAction<TEmployeeManagement[]>) => {
        state.loading = false;
        state.listEmployeeNotJoin = action.payload;
      },
    );
    builder.addCase(addListEmployeeJoinEvent.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(addListEmployeeJoinEvent.fulfilled, (state) => {
      state.loading = false;
    });
  },
});

export const { actions, reducer: AdditionalInterviewReducer } = slice;
export const {
  setListAdditionalInterview,
  setLoading,
  setListEmployeeProgress,
  setIsShowListEvent,
  setFacilityName,
  setEmployeeName,
  setListAdditionalInterviewByFacility,
  setListCalenderFree,
  setListConsultantsFree,
  setListAllConsultants,
  setErrMsg,
  resetErrMsg,
  resetListAdditionalInterview,
  setPage,
  setCurrentLoad,
  setLoadingDownload,
  setLoadingDownloadCSV,
  setGroupMultiSelected,
} = actions;
export default AdditionalInterviewReducer;
